import React, { useEffect, useState } from 'react'

interface AIProcessingProps {
  isVisible: boolean
  agentName: string
}

const PROCESSING_MESSAGES = [
  "🧠 ODC AI is analyzing your request...",
  "⚡ Intelligent systems are activating...",
  "🔍 Processing your business context...",
  "✨ Crafting personalized content...",
  "📊 Running advanced algorithms...",
  "🚀 Almost there...",
  "🎯 Applying final optimizations...",
]

const AIProcessing: React.FC<AIProcessingProps> = ({ isVisible, agentName }) => {
  const [messageIndex, setMessageIndex] = useState(0)
  const [dots, setDots] = useState('')
  const [progress, setProgress] = useState(0)
  const [particles, setParticles] = useState<Array<{id: number, x: number, y: number, size: number, delay: number, color: string}>>([])

  useEffect(() => {
    const colors = ['#BE5103', '#FFCE1B', '#069494', '#B7410E', '#E8721A']
    const newParticles = Array.from({length: 25}, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 5 + 2,
      delay: Math.random() * 3,
      color: colors[Math.floor(Math.random() * colors.length)]
    }))
    setParticles(newParticles)
  }, [])

  useEffect(() => {
    if (!isVisible) {
      setProgress(0)
      setMessageIndex(0)
      setDots('')
      return
    }

    const progressInterval = setInterval(() => {
      setProgress(prev => prev >= 90 ? 90 : prev + Math.random() * 6)
    }, 500)

    const messageInterval = setInterval(() => {
      setMessageIndex(prev => (prev + 1) % PROCESSING_MESSAGES.length)
    }, 1800)

    const dotsInterval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.')
    }, 400)

    return () => {
      clearInterval(progressInterval)
      clearInterval(messageInterval)
      clearInterval(dotsInterval)
    }
  }, [isVisible])

  if (!isVisible) return null

  return (
    <>
      <style>{`
        @keyframes odc-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes odc-spin-reverse { from { transform: rotate(360deg); } to { transform: rotate(0deg); } }
        @keyframes odc-pulse { 0%,100% { transform: scale(1); opacity:1; } 50% { transform: scale(1.15); opacity:0.8; } }
        @keyframes odc-float { 0% { transform: translateY(0px) translateX(0px) scale(1); opacity:0.3; } 50% { transform: translateY(-30px) translateX(15px) scale(1.2); opacity:0.6; } 100% { transform: translateY(-10px) translateX(-10px) scale(0.9); opacity:0.2; } }
        @keyframes odc-scan { 0% { top: -10%; opacity:0; } 5% { opacity:1; } 95% { opacity:1; } 100% { top: 110%; opacity:0; } }
        @keyframes odc-shimmer-line { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
        @keyframes odc-fadeIn { from { opacity:0; transform: scale(0.95); } to { opacity:1; transform: scale(1); } }
        @keyframes odc-glow { 0%,100% { box-shadow: 0 0 20px rgba(190,81,3,0.3); } 50% { box-shadow: 0 0 60px rgba(190,81,3,0.6), 0 0 30px rgba(255,206,27,0.3); } }
      `}</style>

      {/* Backdrop */}
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(2,12,27,0.88)',
        backdropFilter: 'blur(16px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {/* Floating particles */}
        {particles.map(p => (
          <div key={p.id} style={{
            position: 'absolute',
            left: `${p.x}%`, top: `${p.y}%`,
            width: p.size, height: p.size,
            borderRadius: '50%',
            background: p.color,
            animation: `odc-float ${2 + p.delay}s ease-in-out infinite alternate`,
            animationDelay: `${p.delay}s`,
            filter: 'blur(1px)',
            pointerEvents: 'none',
          }} />
        ))}

        {/* Main card */}
        <div style={{
          background: 'rgba(10,20,40,0.95)',
          border: '1px solid rgba(190,81,3,0.35)',
          borderRadius: 28,
          padding: '52px 60px',
          maxWidth: 500,
          width: '90%',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          animation: 'odc-fadeIn 0.3s ease, odc-glow 3s ease-in-out infinite',
        }}>

          {/* Top shimmer line */}
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: 2,
            background: 'linear-gradient(90deg, transparent, #BE5103, #FFCE1B, #069494, transparent)',
            overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)',
              animation: 'odc-shimmer-line 2s linear infinite',
            }} />
          </div>

          {/* Scanning line */}
          <div style={{
            position: 'absolute', left: 0, right: 0, height: 80,
            background: 'linear-gradient(180deg, transparent, rgba(190,81,3,0.05), transparent)',
            animation: 'odc-scan 2.5s ease-in-out infinite',
            pointerEvents: 'none',
          }} />

          {/* Rotating rings */}
          <div style={{ width: 120, height: 120, margin: '0 auto 32px', position: 'relative' }}>
            {/* Ring 1 */}
            <div style={{
              position: 'absolute', inset: 0, borderRadius: '50%',
              border: '2px solid transparent',
              borderTopColor: '#BE5103', borderRightColor: 'rgba(190,81,3,0.3)',
              animation: 'odc-spin 2s linear infinite',
            }} />
            {/* Ring 2 */}
            <div style={{
              position: 'absolute', inset: 10, borderRadius: '50%',
              border: '2px solid transparent',
              borderTopColor: '#FFCE1B', borderLeftColor: 'rgba(255,206,27,0.3)',
              animation: 'odc-spin-reverse 1.5s linear infinite',
            }} />
            {/* Ring 3 */}
            <div style={{
              position: 'absolute', inset: 20, borderRadius: '50%',
              border: '2px solid transparent',
              borderTopColor: '#069494', borderRightColor: 'rgba(6,148,148,0.3)',
              animation: 'odc-spin 1s linear infinite',
            }} />
            {/* Center */}
            <div style={{
              position: 'absolute', inset: 30,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(190,81,3,0.2), rgba(6,148,148,0.2))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 28,
              animation: 'odc-pulse 1.5s ease-in-out infinite',
              border: '1px solid rgba(190,81,3,0.3)',
            }}>
              🤖
            </div>
          </div>

          {/* Agent badge */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'rgba(190,81,3,0.12)',
            border: '1px solid rgba(190,81,3,0.3)',
            borderRadius: 20, padding: '4px 14px',
            fontSize: 11, fontWeight: 700,
            letterSpacing: 1.5, textTransform: 'uppercase',
            color: '#FFCE1B', marginBottom: 16,
          }}>
            ⚡ {agentName}
          </div>

          {/* Title */}
          <h2 style={{
            fontSize: 24, fontWeight: 800, color: '#fff',
            marginBottom: 8, letterSpacing: -0.5,
            fontFamily: "'Space Grotesk', sans-serif",
          }}>
            ODC AI Processing
          </h2>

          {/* Dynamic message */}
          <p style={{
            fontSize: 14, color: 'rgba(255,255,255,0.55)',
            marginBottom: 32, minHeight: 22,
            transition: 'opacity 0.3s ease',
          }}>
            {PROCESSING_MESSAGES[messageIndex]}
          </p>

          {/* Progress bar */}
          <div style={{
            background: 'rgba(255,255,255,0.05)',
            borderRadius: 8, height: 6, marginBottom: 10,
            overflow: 'hidden', border: '1px solid rgba(255,255,255,0.05)',
          }}>
            <div style={{
              height: '100%', borderRadius: 8,
              background: 'linear-gradient(90deg, #BE5103, #FFCE1B, #069494)',
              width: `${progress}%`,
              transition: 'width 0.5s cubic-bezier(0.4,0,0.2,1)',
              boxShadow: '0 0 12px rgba(190,81,3,0.6)',
              position: 'relative', overflow: 'hidden',
            }}>
              <div style={{
                position: 'absolute', inset: 0,
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                animation: 'odc-shimmer-line 1.5s linear infinite',
              }} />
            </div>
          </div>

          {/* Status text */}
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>
            ODC AI is working its magic{dots}
          </p>

          {/* Bottom decoration */}
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, height: 1,
            background: 'linear-gradient(90deg, transparent, rgba(6,148,148,0.4), transparent)',
          }} />
        </div>
      </div>
    </>
  )
}

export default AIProcessing
